<script lang="ts" setup>
import { computed } from "vue";

import ArticleContent from "@/components/article/ArticleContent.vue";
import ArticleContentCollapser from "@/components/article/ArticleContentCollapser.vue";
import ContentsWithLinks from "@/components/article/politiken/ContentsWithLinks.vue";
import {
  type PolitikenDanishEnglishArticle,
  type PolitikenDanishEnglishMeaning
} from "@/types/article-formats/politiken";

const props = defineProps<{
  article: PolitikenDanishEnglishArticle;
  isCollapsible: boolean;
}>();

const hasMeaningNumbers = computed<boolean>(() =>
  props.article.meanings.some((meaning) => meaning.bnr22)
);

interface Translation {
  prefixComment: string | undefined;
  text: string | undefined;
  suffixComment: string | undefined;
  example: string | undefined;
}

const buildTranslation = (
  prefixComment: string,
  text: string,
  suffixComment: string,
  example: string | undefined
): Translation | null => {
  const hasPrefixComment = prefixComment !== "";
  const hasTranslation = text !== "";
  const hasSuffixComment = suffixComment !== "";
  const hasExample = example !== undefined && example !== "";

  if (!hasPrefixComment && !hasTranslation && !hasSuffixComment && !hasExample) {
    return null;
  }

  return {
    prefixComment,
    text,
    suffixComment,
    example
  };
};

const getTranslations = (meaning: PolitikenDanishEnglishMeaning): Translation[] =>
  [
    buildTranslation(meaning.comment1, meaning.translation1, meaning.newComment1, meaning.exa1),
    buildTranslation(meaning.comment2, meaning.translation2, meaning.newComment2, meaning.exa2),
    buildTranslation(meaning.comment3, meaning.translation3, meaning.newComment3, meaning.exa3),
    buildTranslation(meaning.comment4, meaning.translation4, meaning.newComment4, meaning.exa4),
    buildTranslation(meaning.comment5, meaning.translation5, meaning.newComment5, meaning.exa5)
  ].filter((translation): translation is Translation => translation !== null);
</script>

<template>
  <!-- eslint-disable vue/no-v-html -->
  <ArticleContent>
    <div class="article-content__header article-header">
      <div class="article-header__content">
        <h5 class="article-header__word">{{ article.word.lemma }}</h5>
        <sup v-if="article.word.hnr" class="pol-header-sup">{{ article.word.hnr }}</sup>

        <span
          v-if="article.word.star"
          class="article-header__element article-header__element--unchained"
        >
          {{ article.word.star }}
        </span>

        <span
          v-if="article.word.lemmaPronunciation"
          class="article-header__element article-header__element--comment article-header__element--chained"
          v-html="article.word.lemmaPronunciation"
        ></span>

        <template v-if="article.word.alternative">
          <h5
            class="article-header__word article-header__element article-header__element--chained pol-plain"
          >
            el.
          </h5>
          <h5
            class="article-header__word article-header__element article-header__element--chained"
            v-html="article.word.alternative"
          ></h5>
        </template>

        <span
          v-if="article.word.pronunciation"
          class="article-header__element article-header__element--comment article-header__element--chained"
          v-html="article.word.pronunciation"
        ></span>

        <span v-if="article.word.flexion" class="article-header__inflections">
          &lt;<span v-html="article.word.flexion"></span>&gt;
        </span>

        <span
          v-if="article.word.flexionPronunciation"
          class="article-header__element article-header__element--comment article-header__element--chained"
          v-html="article.word.flexionPronunciation"
        ></span>

        <span
          v-if="props.article.word.abbreviation"
          class="article-header__element article-header__element--comment article-header__element--chained"
        >
          (<span v-html="props.article.word.abbreviation"></span>)
        </span>

        <span
          v-if="props.article.word.wordClass"
          class="article-header__element article-header__element--light article-header__element--italic"
        >
          {{ props.article.word.wordClass }}
        </span>

        <span
          v-if="article.word.reference.length > 0"
          class="article-header__element article-header__element--comment article-header__element--chained"
        >
          (<ContentsWithLinks
            v-for="(reference, index) in article.word.reference"
            :key="index"
            :content="reference"
            link-class="article-content__link"
          />)
        </span>
      </div>
    </div>

    <ArticleContentCollapser :is-collapsible="props.isCollapsible">
      <div class="article-content__body-container article-content__body-container--reading-lines">
        <div
          v-for="(meaning, meaningIndex) in article.meanings"
          :key="meaningIndex"
          class="indentation-container"
        >
          <div :class="{ 'indentation-container': hasMeaningNumbers }">
            <span
              v-if="meaning.bnr22"
              class="article-list-bullet article-list-bullet--primary article-list-bullet--prominent"
            >
              {{ meaning.bnr22 }}.
            </span>

            <span
              v-if="meaning.comment0"
              class="article-content__text article-content__text--light"
            >
              (<span v-html="meaning.comment0"></span>)
            </span>

            <span
              v-if="meaning.construction"
              class="article-content__text article-content__text--primary article-content__text--prominent"
              v-html="meaning.construction"
            >
            </span>

            <span
              v-if="meaning.abbreviation"
              class="article-content__text article-content__text--light"
            >
              (<span v-html="meaning.abbreviation"></span>)
            </span>

            <span
              v-for="(translation, translationIndex) in getTranslations(meaning)"
              :key="translationIndex"
              :class="{ 'd-block': translationIndex > 0 }"
            >
              =

              <span
                v-if="translation.prefixComment"
                class="article-content__text article-content__text--light mr-1"
                >(<span v-html="translation.prefixComment"></span>)</span
              >

              <span
                class="article-content__text article-content__text--primary"
                v-html="translation.text"
              ></span>

              <span
                v-if="translation.suffixComment"
                class="article-content__text article-content__text--light"
              >
                (<span v-html="translation.suffixComment"></span>)
              </span>

              <span
                v-if="translation.example"
                class="article-content__text article-content__text--italic"
              >
                <span class="mx-2">&#9633;</span><span v-html="translation.example"></span>
              </span>
            </span>

            <div
              v-if="meaning.englishAbbreviation"
              class="article-content__text article-content__text--muted"
              v-html="meaning.englishAbbreviation"
            ></div>

            <div
              v-if="meaning.danishExample"
              class="article-content__text article-content__text--italic"
              v-html="meaning.danishExample"
            ></div>

            <div
              v-if="meaning.example"
              class="article-content__text article-content__text--italic"
              v-html="meaning.example"
            ></div>

            <span
              v-if="meaning.reference.length > 0"
              class="article-content__text article-content__text--muted"
            >
              <ContentsWithLinks
                v-for="(reference, index) in meaning.reference"
                :key="index"
                :content="reference"
                link-class="article-content__link"
              />
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="article.word.boxHeadline || article.word.boxText"
        class="article-content__body-container"
      >
        <div class="article-content__politiken-box">
          <div class="article-content__politiken-box-header" v-html="article.word.boxHeadline" />
          <div class="article-content__politiken-box-content" v-html="article.word.boxText" />
        </div>
        <div class="article-content__spacer" />
      </div>
    </ArticleContentCollapser>
  </ArticleContent>
</template>
